import React, { useState } from "react";

const ReactNative = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn to build
          cross-platform mobile applications using React Native.
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          JavaScript, React, and programming concepts.
          <p className="font-bold text-sm">Tools Needed:</p> Node.js, React
          Native CLI, Expo, Android Studio/Xcode, and a mobile device/emulator.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to React Native",
          objective: "Understand React Native and its advantages.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is React Native? Why use it?</li>
                <li>React Native vs. native development.</li>
                <li>Overview of cross-platform development.</li>
                <li>Setting up the development environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Discuss real-world React Native apps.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Setting Up React Native Environment",
          objective:
            "Set up tools and environment for React Native development.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing Node.js, npm, and React Native CLI.</li>
                <li>Setting up Expo or React Native CLI project.</li>
                <li>Configuring Android Studio/Xcode.</li>
                <li>Running an emulator or connecting a device.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a new React Native project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Your First React Native App",
          objective: "Create a basic React Native application.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding project structure.</li>
                <li>Creating a simple "Hello World" app.</li>
                <li>Running the app on emulator/device.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build and run a "Hello World" app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: React Native Components",
          objective: "Learn core React Native components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>View, Text, Image, and Button components.</li>
                <li>Styling components with StyleSheet.</li>
                <li>Flexbox layout in React Native.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a layout with View and Text.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Styling in React Native",
          objective: "Master styling techniques in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Inline styles vs. StyleSheet.</li>
                <li>Using dimensions and positioning.</li>
                <li>Responsive design with Flexbox.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Style a component with Flexbox.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: State and Props",
          objective: "Understand state and props in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are state and props?</li>
                <li>Using useState hook.</li>
                <li>Passing props between components.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a counter with useState.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Event Handling",
          objective: "Learn to handle events in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling touch events (onPress, etc.).</li>
                <li>Form handling with TextInput.</li>
                <li>Preventing default behavior.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a form with TextInput.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Conditional Rendering",
          objective: "Implement conditional rendering in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using if, &&, and ternary operators.</li>
                <li>Rendering based on state.</li>
                <li>Loading and error states.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Show/hide content with a button.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Lists and FlatList",
          objective: "Learn to render lists in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rendering lists with map.</li>
                <li>Using FlatList for performance.</li>
                <li>Key concepts in lists.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Display a list with FlatList.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Navigation Basics",
          objective: "Understand navigation in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to React Navigation.</li>
                <li>Setting up stack navigator.</li>
                <li>Navigating between screens.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a stack navigator.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Advanced Navigation",
          objective: "Explore advanced navigation features.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Tab navigation and drawer navigation.</li>
                <li>Passing params between screens.</li>
                <li>Customizing navigation headers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a tab navigator.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Managing State with Context",
          objective: "Learn global state management with Context.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a Context.</li>
                <li>Providing and consuming Context.</li>
                <li>Managing app-wide state.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Context for user data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Using useReducer",
          objective: "Manage complex state with useReducer.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is useReducer?</li>
                <li>Combining with Context.</li>
                <li>Handling complex state logic.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement a reducer for a list.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: API Integration",
          objective: "Fetch data from APIs in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using fetch or Axios.</li>
                <li>Making GET requests.</li>
                <li>Displaying API data.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Fetch and display a list from an API.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: POST Requests",
          objective: "Send POST requests in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building a form for POST requests.</li>
                <li>Handling form submissions.</li>
                <li>Sending data to an API.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a form to submit data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: PUT and DELETE Requests",
          objective: "Handle PUT and DELETE requests.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Updating data with PUT.</li>
                <li>Deleting data with DELETE.</li>
                <li>Updating UI after requests.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add edit/delete buttons to a list.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Handling Loading and Errors",
          objective: "Manage loading and error states.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling loading states.</li>
                <li>Displaying error messages.</li>
                <li>Conditional rendering for states.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add loading/error states to an API call.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Custom Components",
          objective: "Create reusable custom components.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building custom components.</li>
                <li>Passing props to components.</li>
                <li>Styling custom components.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a reusable button component.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Animations",
          objective: "Add animations to React Native apps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Animated API.</li>
                <li>Creating simple animations.</li>
                <li>Using Animated.Value.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a fade-in animation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Gesture Responder System",
          objective: "Handle gestures in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding gestures.</li>
                <li>Using PanResponder.</li>
                <li>Handling touch events.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a draggable component.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Local Storage with AsyncStorage",
          objective: "Manage local data with AsyncStorage.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is AsyncStorage?</li>
                <li>Storing and retrieving data.</li>
                <li>Handling asynchronous operations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Store and retrieve user preferences.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Debugging React Native Apps",
          objective: "Learn debugging techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using React Native Debugger.</li>
                <li>Console logging and breakpoints.</li>
                <li>Remote debugging with Chrome.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Debug a simple app issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Performance Optimization",
          objective: "Optimize React Native app performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Improving list performance.</li>
                <li>Using memo and useCallback.</li>
                <li>Profiling with React Native tools.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a FlatList component.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Third-Party Libraries",
          objective: "Integrate third-party libraries.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing libraries with npm.</li>
                <li>Using popular libraries (e.g., Lodash).</li>
                <li>Managing dependencies.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a library to enhance functionality.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Push Notifications",
          objective: "Implement push notifications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to push notifications.</li>
                <li>Using Expo Notifications.</li>
                <li>Configuring Firebase for notifications.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a basic notification.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Camera and Media Access",
          objective: "Access camera and media in React Native.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Camera API.</li>
                <li>Capturing photos and videos.</li>
                <li>Accessing media library.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a photo capture feature.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Geolocation",
          objective: "Implement geolocation features.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Geolocation API.</li>
                <li>Getting current location.</li>
                <li>Handling location updates.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Display current location on a map.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Maps Integration",
          objective: "Integrate maps into React Native apps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using react-native-maps.</li>
                <li>Displaying maps.</li>
                <li>Adding markers and overlays.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a map with a marker.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Authentication Basics",
          objective: "Implement basic authentication.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to authentication.</li>
                <li>Using Firebase Authentication.</li>
                <li>Login and signup flows.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a login screen.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Secure Data Handling",
          objective: "Learn secure data practices.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Encrypting sensitive data.</li>
                <li>Using secure storage.</li>
                <li>Handling API tokens.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Securely store an API token.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Testing React Native Apps",
          objective: "Learn to test React Native applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Jest and React Native Testing Library.</li>
                <li>Writing unit tests.</li>
                <li>Running tests on CI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a test for a component.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Code Push Deployment",
          objective: "Deploy updates with CodePush.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CodePush?</li>
                <li>Setting up CodePush.</li>
                <li>Deploying over-the-air updates.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a CodePush update.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Building for Production",
          objective: "Prepare the app for production.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Generating APK/IPA files.</li>
                <li>Configuring app icons and splash screens.</li>
                <li>Signing the app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build an APK file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: App Store Deployment",
          objective: "Deploy to the App Store.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing for App Store submission.</li>
                <li>Using App Store Connect.</li>
                <li>Submitting the iOS app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Submit an app to TestFlight.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Google Play Deployment",
          objective: "Deploy to Google Play.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing for Google Play submission.</li>
                <li>Using Google Play Console.</li>
                <li>Submitting the Android app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Submit an app to Google Play.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: CI/CD for React Native",
          objective: "Set up CI/CD pipelines.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CI/CD?</li>
                <li>Using GitHub Actions.</li>
                <li>Automating builds and deployments.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a CI/CD pipeline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., To-Do App, E-commerce).</li>
                <li>Planning UI and features.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and wireframe.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – UI Development",
          objective: "Build the UI for the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Designing screens.</li>
                <li>Using custom components.</li>
                <li>Styling the app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the UI screens.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Final Project – Functionality",
          objective: "Add functionality to the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding state and navigation.</li>
                <li>Integrating APIs.</li>
                <li>Implementing features.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add core functionality.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Final Project – Testing",
          objective: "Test the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Writing unit and integration tests.</li>
                <li>Testing on multiple devices.</li>
                <li>Fixing bugs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and debug the app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Final Project – Optimization",
          objective: "Optimize the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Performance tuning.</li>
                <li>Reducing bundle size.</li>
                <li>Ensuring responsiveness.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize the app performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Final Project – Deployment Prep",
          objective: "Prepare for deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Generating production builds.</li>
                <li>Configuring app assets.</li>
                <li>Signing the app.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Prepare the app for submission.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – App Store Submission",
          objective: "Submit to the App Store.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>App Store Connect setup.</li>
                <li>Uploading the iOS app.</li>
                <li>Review process.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Submit to TestFlight.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Google Play Submission",
          objective: "Submit to Google Play.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Google Play Console setup.</li>
                <li>Uploading the Android app.</li>
                <li>Review process.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Submit to Google Play.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed app.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Build cross-platform mobile apps with React Native.</li>
            <li>Implement navigation and state management.</li>
            <li>Integrate APIs and handle media.</li>
            <li>Deploy apps to App Store and Google Play.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">To-Do App:</span> A task
              management app with reminders and categories.
            </li>
            <li>
              <span className="font-bold text-sm">E-commerce App:</span> A
              shopping app with product listings and cart.
            </li>
            <li>
              <span className="font-bold text-sm">Fitness Tracker:</span> An app
              to log workouts and track progress.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Encourage practice with mini-projects outside class.</li>
            <li>Provide resources (e.g., docs, tutorials).</li>
            <li>Use discussion time for Q&A and clarifications.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from basics to advanced topics, ensuring
            practical skills in React Native development over 45 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-purple-300 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          React Native Syllabus
        </h1>
        <p className="text-sm text-white">
          Master Mobile Development with React Native (45 Hours – 1 Hour Per
          Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactNative;
